/**
 * DirectionProvider — sets <html> dir/lang attributes and provides direction context.
 * @layer-1 - direction context. All themed components use this via useDirection().
 */
import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface DirectionContextValue {
  isRTL: boolean;
  dir: "rtl" | "ltr";
  language: string;
}

const DirectionContext = createContext<DirectionContextValue>({
  isRTL: false,
  dir: "ltr",
  language: "en",
});

export function useDirection() {
  return useContext(DirectionContext);
}

interface DirectionProviderProps {
  children: ReactNode;
}

export function DirectionProvider({ children }: DirectionProviderProps) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const dir = isRTL ? "rtl" : "ltr";
  const language = i18n.language;

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = dir;
  }, [language, dir]);

  return (
    <DirectionContext.Provider value={{ isRTL, dir, language }}>
      {children}
    </DirectionContext.Provider>
  );
}
